/**
 * Compact store for the legacy heavy `POLYLINE` (AcDb2dPolyline /
 * AcDb3dPolyline / AcDbPolygonMesh / AcDbPolyFaceMesh).
 *
 * # Why this store has to exist
 *
 * On the streaming import path (`≥ 2 MB` DXF → `dxf-parser.worker.ts`) the
 * bridge runs with `discardEntitiesAfterStreaming: true`, so
 * `space.entities[]` is left EMPTY and the read-through wrapper rebuilds
 * every entity out of the compact store. An entity type with no store here
 * is silently dropped by `appendEntity`'s `default:` arm (`counts.Other++`,
 * return `-1`) and therefore never reaches the document in the browser —
 * which is exactly how the first cut of the heavy-polyline / table / OLE
 * work passed its non-streaming tests and still imported 230 of 243
 * entities in a real browser.
 *
 * # Presence, not sentinels
 *
 * DXF distinguishes "group 40 absent ⇒ inherit the polyline's default width"
 * from "group 40 written as 0 ⇒ this vertex really is zero-width", and the
 * parser preserves that distinction deliberately. A store that wrote 0 for
 * an absent optional would destroy it on the streaming path only, so every
 * optional scalar here carries a PRESENCE BIT and is re-emitted only when it
 * was authored.
 *
 * # Layout
 *
 * Follows the CSR (offset/length) convention the sibling variable-length
 * stores use (`compactPolylineStores.ts`, `compactCoverageStores.ts`):
 * per-entity scalars in typed arrays, the vertex run in flat buffers indexed
 * by a per-entity offset column, and genuinely SPARSE per-vertex extras in
 * `Map`s keyed by the global vertex slot — the same shape
 * `compactVertexPacking.ts` uses for `vertexIdData`.
 *
 * `Float32Array` for coordinates matches every sibling store (LINE included);
 * this module does not introduce a second precision convention.
 */

import type {
  DrawingPoint3D,
  PolylineEntity,
  PolylineSeqendMetadata,
  PolylineVertexRecord,
} from "@dxf-ai-editor/drawing-model";

/**
 * Everything a `PolylineVertexRecord` carries that the per-vertex COLUMNS
 * above do not — today the four DXF passthrough channels (`subclassMarkers`,
 * `extraGroups`, `xdata`, `extensionDictionaryHandle`).
 *
 * Derived by REST destructuring the columnar keys rather than by listing the
 * passthrough keys, and that is the point: a channel added to
 * `polylineVertexRecordSchema` tomorrow lands here automatically instead of
 * being silently dropped on the streaming path until someone notices. The
 * type is `Omit<…>` of the same key set the destructuring removes, so the two
 * cannot drift without a compile error.
 */
type PolylineVertexColumnKey =
  | "handle"
  | "location"
  | "startWidth"
  | "endWidth"
  | "bulge"
  | "flags"
  | "curveFitTangent"
  | "faceVertexIndices"
  | "vertexId";
type PolylineVertexMetadata = Omit<
  PolylineVertexRecord,
  PolylineVertexColumnKey
>;

import {
  BASE_BYTES_PER_ENTITY,
  createBaseColumns,
  growBaseColumns,
  readBase,
  writeBase,
  type BaseColumns,
} from "./compactStoreBase";
import type { SharedStringTable } from "./sharedStringTable";

/** Per-entity floats: elevation x/y/z, thickness, default start/end width. */
const PL_FLOATS = 6;
/** Per-entity ints: flags, mesh M/N, smooth M/N density, surface type, group 66. */
const PL_INTS = 7;
/** Per-vertex floats: x, y, z, bulge, startWidth, endWidth, curveFitTangent. */
const PL_VERTEX_FLOATS = 7;

/** Presence bits for the entity's optional scalars. */
const HAS_DEFAULT_START_WIDTH = 1 << 0;
const HAS_DEFAULT_END_WIDTH = 1 << 1;
const HAS_MESH_M = 1 << 2;
const HAS_MESH_N = 1 << 3;
const HAS_SMOOTH_M = 1 << 4;
const HAS_SMOOTH_N = 1 << 5;
const HAS_SURFACE_TYPE = 1 << 6;
const HAS_VERTICES_FOLLOW = 1 << 7;

/** Presence bits for one VERTEX record's optional fields. */
const V_HAS_BULGE = 1 << 0;
const V_HAS_START_WIDTH = 1 << 1;
const V_HAS_END_WIDTH = 1 << 2;
const V_HAS_CURVE_FIT_TANGENT = 1 << 3;
const V_HAS_FLAGS = 1 << 4;
const V_HAS_VERTEX_ID = 1 << 5;
const V_HAS_FACE_INDICES = 1 << 6;

export interface CompactHeavyPolylineStore {
  count: number;
  base: BaseColumns;
  floats: Float32Array;
  ints: Int32Array;
  /** One byte of `HAS_*` presence bits per entity. */
  present: Uint8Array;
  /** Sparse — only a polyline whose file carried 210/220/230. */
  extrusions: Map<number, DrawingPoint3D>;
  /** Sparse — the terminating SEQEND's handle, kept so it round-trips. */
  seqendHandles: Map<number, string>;
  /**
   * Sparse — the REST of the terminating SEQEND record: its own layer, XDATA,
   * extension dictionary, reactor block, unmapped groups and subclass
   * extensions (`PolylineSeqendMetadata`, itself an `Omit` over the keys the
   * bridge re-derives, so a channel added to the SEQEND model later rides
   * along inside the carried object instead of needing a column here).
   *
   * Load-bearing for the SAME reason `vertexMetadata` below is, and it is the
   * SAME gap one record further along: a ≥ `STREAMING_IMPORT_FILE_SIZE_
   * THRESHOLD` file is imported with `discardEntitiesAfterStreaming`, so the
   * document's own `space.entities[]` are thrown away and every entity the
   * user then exports is rebuilt from THIS store. Without this column the DXF
   * bridge's SEQEND preservation (@codex PR #785 round 2 P1) held for a small
   * file and was deleted on open-and-save of a big one — for exactly the
   * drawings this store exists to serve. Held by reference, like
   * `extrusions` / `vertexFaceIndices` / `vertexMetadata`: the value is
   * immutable passthrough data the reader hands straight back.
   */
  seqendMetadata: Map<number, PolylineSeqendMetadata>;

  vertexFloats: Float32Array;
  vertexInts: Int32Array;
  /** One byte of `V_HAS_*` presence bits per VERTEX slot. */
  vertexPresent: Uint8Array;
  /**
   * VERTEX handle per slot (group 5), held DIRECTLY — not interned.
   *
   * A DXF handle is unique BY CONSTRUCTION (Autodesk DXF reference, group 5:
   * "Handle … unique within the drawing database"; ezdxf 1.4.4 `document.py`
   * hands every entity a fresh handle out of `HandleGenerator`), so the shared
   * string table can never dedupe one: interning a handle spends a table slot,
   * an `index` Map entry AND the string itself to save nothing, and the table's
   * ids are 16-bit, so ~65 536 of them exhausted it and made `internString`
   * THROW `sharedStringTable capacity exceeded` — killing the import of
   * exactly the large drawing this store exists to open (@codex round 6 P1).
   *
   * So handles live here the same way `compactStoreBase` holds `id` /
   * `spaceId` and this store already holds `seqendHandles`: a plain string
   * column with no id ceiling, and strictly cheaper than interning, since the
   * table entry and its Map key are pure overhead for a string nothing reuses.
   * The table stays reserved for the genuinely REPEATED strings interning is
   * for — layer, linetype, lineweight, transparency, colour, style names.
   */
  vertexHandles: string[];
  /** Poly-face FACE indices, sparse — only a FACE record carries 71..74. */
  vertexFaceIndices: Map<number, number[]>;
  /**
   * Per-VERTEX records with no column of their own — the DXF passthrough
   * channels (`102 {ACAD_XDICTIONARY` / `102 {ACAD_REACTORS` / `1001` XDATA /
   * a non-canonical `100` subclass extension). Sparse: real drawings attach
   * them to a handful of vertices at most, and a vertex that carried none
   * holds no entry.
   *
   * Load-bearing, not a nicety. A ≥ `STREAMING_IMPORT_FILE_SIZE_THRESHOLD`
   * file is imported with `discardEntitiesAfterStreaming`, so the document's
   * own `space.entities[]` are thrown away and every entity the user then
   * exports is rebuilt from THIS store. A channel missing here is a channel
   * deleted from the saved file — for exactly the big drawings this store
   * exists to serve. Held by reference, like `vertexFaceIndices`.
   */
  vertexMetadata: Map<number, PolylineVertexMetadata>;
  /** CSR: `[offsets[i], offsets[i + 1])` is entity i's vertex run. */
  vertexOffsets: Uint32Array;
}

export function createCompactHeavyPolylineStore(
  initialCapacity = 32,
): CompactHeavyPolylineStore {
  const cap = Math.max(1, initialCapacity);
  return {
    count: 0,
    base: createBaseColumns(cap),
    floats: new Float32Array(cap * PL_FLOATS),
    ints: new Int32Array(cap * PL_INTS),
    present: new Uint8Array(cap),
    extrusions: new Map(),
    seqendHandles: new Map(),
    seqendMetadata: new Map(),
    vertexFloats: new Float32Array(256 * PL_VERTEX_FLOATS),
    vertexInts: new Int32Array(256 * 2),
    vertexPresent: new Uint8Array(256),
    vertexHandles: new Array<string>(256).fill(""),
    vertexFaceIndices: new Map(),
    vertexMetadata: new Map(),
    vertexOffsets: new Uint32Array(cap + 1),
  };
}

function ensureEntityCap(s: CompactHeavyPolylineStore, needed: number): void {
  if (needed <= s.base.capacity) return;
  const c = growBaseColumns(s.base, needed);
  const f = new Float32Array(c * PL_FLOATS); f.set(s.floats);
  const i = new Int32Array(c * PL_INTS); i.set(s.ints);
  const p = new Uint8Array(c); p.set(s.present);
  const o = new Uint32Array(c + 1); o.set(s.vertexOffsets);
  s.floats = f; s.ints = i; s.present = p; s.vertexOffsets = o;
}

function ensureVertexCap(s: CompactHeavyPolylineStore, needed: number): void {
  if (needed <= s.vertexPresent.length) return;
  let cap = Math.max(s.vertexPresent.length, 1);
  while (cap < needed) cap *= 2;
  const vf = new Float32Array(cap * PL_VERTEX_FLOATS); vf.set(s.vertexFloats);
  const vi = new Int32Array(cap * 2); vi.set(s.vertexInts);
  const vp = new Uint8Array(cap); vp.set(s.vertexPresent);
  const oldLen = s.vertexHandles.length;
  s.vertexHandles.length = cap;
  for (let slot = oldLen; slot < cap; slot++) s.vertexHandles[slot] = "";
  s.vertexFloats = vf; s.vertexInts = vi; s.vertexPresent = vp;
}

export function appendHeavyPolyline(
  s: CompactHeavyPolylineStore,
  table: SharedStringTable,
  e: PolylineEntity,
): number {
  const index = s.count;
  ensureEntityCap(s, index + 1);
  s.count++;
  writeBase(s.base, table, index, e);

  const f = index * PL_FLOATS;
  s.floats[f + 0] = e.elevation.x;
  s.floats[f + 1] = e.elevation.y;
  s.floats[f + 2] = e.elevation.z;
  s.floats[f + 3] = e.thickness;
  s.floats[f + 4] = e.defaultStartWidth ?? 0;
  s.floats[f + 5] = e.defaultEndWidth ?? 0;

  const n = index * PL_INTS;
  s.ints[n + 0] = e.flags;
  s.ints[n + 1] = e.meshMCount ?? 0;
  s.ints[n + 2] = e.meshNCount ?? 0;
  s.ints[n + 3] = e.smoothSurfaceMDensity ?? 0;
  s.ints[n + 4] = e.smoothSurfaceNDensity ?? 0;
  s.ints[n + 5] = e.surfaceType ?? 0;
  s.ints[n + 6] = e.verticesFollow ?? 0;

  let present = 0;
  if (e.defaultStartWidth !== undefined) present |= HAS_DEFAULT_START_WIDTH;
  if (e.defaultEndWidth !== undefined) present |= HAS_DEFAULT_END_WIDTH;
  if (e.meshMCount !== undefined) present |= HAS_MESH_M;
  if (e.meshNCount !== undefined) present |= HAS_MESH_N;
  if (e.smoothSurfaceMDensity !== undefined) present |= HAS_SMOOTH_M;
  if (e.smoothSurfaceNDensity !== undefined) present |= HAS_SMOOTH_N;
  if (e.surfaceType !== undefined) present |= HAS_SURFACE_TYPE;
  if (e.verticesFollow !== undefined) present |= HAS_VERTICES_FOLLOW;
  s.present[index] = present;

  if (e.extrusion !== undefined) s.extrusions.set(index, e.extrusion);
  else s.extrusions.delete(index);
  if (e.seqendHandle !== undefined) s.seqendHandles.set(index, e.seqendHandle);
  else s.seqendHandles.delete(index);
  if (e.seqendMetadata !== undefined) {
    s.seqendMetadata.set(index, e.seqendMetadata);
  } else {
    s.seqendMetadata.delete(index);
  }

  const start = index === 0 ? 0 : s.vertexOffsets[index]!;
  ensureVertexCap(s, start + e.vertices.length);
  for (let v = 0; v < e.vertices.length; v++) {
    const rec = e.vertices[v]!;
    const slot = start + v;
    const vf = slot * PL_VERTEX_FLOATS;
    s.vertexFloats[vf + 0] = rec.location.x;
    s.vertexFloats[vf + 1] = rec.location.y;
    s.vertexFloats[vf + 2] = rec.location.z;
    s.vertexFloats[vf + 3] = rec.bulge ?? 0;
    s.vertexFloats[vf + 4] = rec.startWidth ?? 0;
    s.vertexFloats[vf + 5] = rec.endWidth ?? 0;
    s.vertexFloats[vf + 6] = rec.curveFitTangent ?? 0;
    const vi = slot * 2;
    s.vertexInts[vi + 0] = rec.flags ?? 0;
    s.vertexInts[vi + 1] = rec.vertexId ?? 0;
    let vp = 0;
    if (rec.bulge !== undefined) vp |= V_HAS_BULGE;
    if (rec.startWidth !== undefined) vp |= V_HAS_START_WIDTH;
    if (rec.endWidth !== undefined) vp |= V_HAS_END_WIDTH;
    if (rec.curveFitTangent !== undefined) vp |= V_HAS_CURVE_FIT_TANGENT;
    if (rec.flags !== undefined) vp |= V_HAS_FLAGS;
    if (rec.vertexId !== undefined) vp |= V_HAS_VERTEX_ID;
    if (rec.faceVertexIndices !== undefined) {
      vp |= V_HAS_FACE_INDICES;
      s.vertexFaceIndices.set(slot, rec.faceVertexIndices);
    } else {
      s.vertexFaceIndices.delete(slot);
    }
    s.vertexPresent[slot] = vp;
    s.vertexHandles[slot] = rec.handle;
    // Whatever the record carries BEYOND the columns above. Rest destructuring
    // (not an allow-list of channel names) so a field added to the model later
    // cannot be silently lost here — see PolylineVertexMetadata.
    const {
      handle: _handle,
      location: _location,
      startWidth: _startWidth,
      endWidth: _endWidth,
      bulge: _bulge,
      flags: _flags,
      curveFitTangent: _curveFitTangent,
      faceVertexIndices: _faceVertexIndices,
      vertexId: _vertexId,
      ...metadata
    } = rec;
    if (Object.keys(metadata).length > 0) s.vertexMetadata.set(slot, metadata);
    else s.vertexMetadata.delete(slot);
  }
  s.vertexOffsets[index] = start;
  s.vertexOffsets[index + 1] = start + e.vertices.length;
  return index;
}

export function heavyPolylineFromCompact(
  s: CompactHeavyPolylineStore,
  table: SharedStringTable,
  index: number,
): PolylineEntity {
  if (index < 0 || index >= s.count) {
    throw new RangeError(
      `heavyPolylineFromCompact: index ${index} out of range (count=${s.count})`,
    );
  }
  const f = index * PL_FLOATS;
  const n = index * PL_INTS;
  const present = s.present[index] ?? 0;

  const start = s.vertexOffsets[index]!;
  const end = s.vertexOffsets[index + 1]!;
  const vertices: PolylineVertexRecord[] = [];
  for (let slot = start; slot < end; slot++) {
    const vf = slot * PL_VERTEX_FLOATS;
    const vi = slot * 2;
    const vp = s.vertexPresent[slot] ?? 0;
    const rec: PolylineVertexRecord = {
      handle: s.vertexHandles[slot] ?? "",
      location: {
        x: s.vertexFloats[vf + 0]!,
        y: s.vertexFloats[vf + 1]!,
        z: s.vertexFloats[vf + 2]!,
      },
    };
    if ((vp & V_HAS_START_WIDTH) !== 0) rec.startWidth = s.vertexFloats[vf + 4]!;
    if ((vp & V_HAS_END_WIDTH) !== 0) rec.endWidth = s.vertexFloats[vf + 5]!;
    if ((vp & V_HAS_BULGE) !== 0) rec.bulge = s.vertexFloats[vf + 3]!;
    if ((vp & V_HAS_FLAGS) !== 0) rec.flags = s.vertexInts[vi + 0]!;
    if ((vp & V_HAS_CURVE_FIT_TANGENT) !== 0) {
      rec.curveFitTangent = s.vertexFloats[vf + 6]!;
    }
    if ((vp & V_HAS_FACE_INDICES) !== 0) {
      rec.faceVertexIndices = s.vertexFaceIndices.get(slot) ?? [];
    }
    if ((vp & V_HAS_VERTEX_ID) !== 0) rec.vertexId = s.vertexInts[vi + 1]!;
    const metadata = s.vertexMetadata.get(slot);
    if (metadata !== undefined) Object.assign(rec, metadata);
    vertices.push(rec);
  }

  const entity: PolylineEntity = {
    ...readBase(s.base, table, index),
    type: "Polyline",
    flags: s.ints[n + 0]!,
    elevation: {
      x: s.floats[f + 0]!,
      y: s.floats[f + 1]!,
      z: s.floats[f + 2]!,
    },
    vertices,
    thickness: s.floats[f + 3]!,
  };
  if ((present & HAS_DEFAULT_START_WIDTH) !== 0) {
    entity.defaultStartWidth = s.floats[f + 4]!;
  }
  if ((present & HAS_DEFAULT_END_WIDTH) !== 0) {
    entity.defaultEndWidth = s.floats[f + 5]!;
  }
  if ((present & HAS_MESH_M) !== 0) entity.meshMCount = s.ints[n + 1]!;
  if ((present & HAS_MESH_N) !== 0) entity.meshNCount = s.ints[n + 2]!;
  if ((present & HAS_SMOOTH_M) !== 0) {
    entity.smoothSurfaceMDensity = s.ints[n + 3]!;
  }
  if ((present & HAS_SMOOTH_N) !== 0) {
    entity.smoothSurfaceNDensity = s.ints[n + 4]!;
  }
  if ((present & HAS_SURFACE_TYPE) !== 0) entity.surfaceType = s.ints[n + 5]!;
  if ((present & HAS_VERTICES_FOLLOW) !== 0) {
    entity.verticesFollow = s.ints[n + 6]!;
  }
  const extrusion = s.extrusions.get(index);
  if (extrusion !== undefined) entity.extrusion = extrusion;
  const seqendHandle = s.seqendHandles.get(index);
  if (seqendHandle !== undefined) entity.seqendHandle = seqendHandle;
  const seqendMetadata = s.seqendMetadata.get(index);
  if (seqendMetadata !== undefined) entity.seqendMetadata = seqendMetadata;
  return entity;
}

export function estimateHeavyPolylineBytesPerEntity(
  s: CompactHeavyPolylineStore,
): number {
  const fixed = PL_FLOATS * 4 + PL_INTS * 4 + 1 + BASE_BYTES_PER_ENTITY;
  if (s.count === 0) return fixed;
  const totalVertices = s.vertexOffsets[s.count]!;
  // `+ 8` is the handle POINTER, matching `BASE_BYTES_PER_ENTITY`'s `2 * 8`
  // for its two `string[]` columns. The handle's own CHARACTERS are added
  // below rather than assumed: they used to be counted by the aggregate
  // estimator's shared-string-table term (`totalCharCount * 2`), and dropping
  // them when the handles left that table would have under-reported the
  // store instead of reporting the saving it actually is.
  const perVertex = PL_VERTEX_FLOATS * 4 + 2 * 4 + 1 + 8;
  let handleChars = 0;
  for (let slot = 0; slot < totalVertices; slot++) {
    handleChars += (s.vertexHandles[slot] ?? "").length * 2;
  }
  return fixed + (totalVertices * perVertex + handleChars) / s.count;
}
